import { Common } from '../common';
import { PackageJsonUpgrader } from './upgrader/package-json-upgrader';
import { TSConfigUpgrader } from './upgrader/tsconfig-upgrader';
import { Logger } from './utils/logger';
import { GitIgnoreUpgrader } from './upgrader/git-ignore-upgrader';
import { AngularJsonUpgrader } from './upgrader/angular-json-upgrader';
import { FileReplacementUpgrader } from './upgrader/file-replacement-upgrader';
import { CIBuildYmlUpgrader } from './upgrader/ci-build-yml-upgrader';
import { ReplaceUpgrader } from './upgrader/replace-upgrader';
import { srcTsConfigLibProdReplaces, srcTsConfigSpecRemoves } from './configs/config';
import { RemoveUpgrader } from './upgrader/remove-upgrader';
import { MainDebugger } from './utils/main-debugger';
import { runCommand } from './utils/command-runner';
import { LogParser } from './upgrader/audit/log-parser';
import { VersionJsonUpgrader } from './upgrader/version-json-upgrader';
import { JasmineTestUpgrader } from './upgrader/jasmine-test-upgrader';
import { updateShellReferences } from './utils/update-shell-references';
import { EslintUpgrader } from '../eslint/main';
import { UITestAutomationLegacyUpgrader } from './upgrader/ui-test-automation-legacy-upgrader';
import { EslintAutoFixUpgrader } from './upgrader/eslint-auto-fix-upgrader';

/**
 * Represents the type of upgrade that needs to be performed.
 */
export enum UpgradeType {
    WithBaseConfig = 'WithBaseConfig',
    WithoutBaseConfig = 'WithoutBaseConfig',
    NoOperation = 'NoOperation'
}

/**
 * Represents the messages that are displayed during the upgrade process.
 */
export enum Angular15UpgraderMessages {
    StartingUpgrade = 'Starting Angular 15 upgrade process...',
    UpdateBasicPaths = 'Updating basic paths for the upgrade...',
    Initialization = 'Initializing upgraders...',
    FinalizeInfoLintFix = `There are eslint violations that needs fix... After fixing the violations, please run the following commands:\n
        npx eslint . --fix \n
        gulp lintApp \n
        gulp build \n
    `,
    InternalCommandOnly = 'This command is only available for internal extension.',
    Completed = 'Upgrade completed!',
    StartMessageStage0 = 'Starting stage 0: the default Angular 15 upgrade stage',
    StartMessageStage1 = 'Starting stage 1: the Angular core stage',
    StartMessageStage2 = 'Starting stage 2: the Angular 15 unit test stage',
    StartMessageStage3 = 'Starting stage 3: the legacy ui-test-automation stage',
    StartMessageStage4 = 'Starting stage 4: the eslint to support gulp lintApp --fix command stage'
}

/**
 * This is the main class of the EslintUpgrader tool.
 * It is responsible for upgrading the Tslint to Eslint.
 */
export class Angular15Upgrader {
    /**
     * The debug mode.
     */
    protected debug = false;

    /**
     * It determines whether to run angualr 15 upgrade for the internal extension or external sdk.
     */
    protected internal = false;

    /**
     * If running in the pipeline, the pipeline needs to automatically update for the @msft-sme pacakges instead of WAC CLI.
     */
    protected pipeline = false;

    /**
     * The main debugger.
     */
    protected mainDebugger: MainDebugger;

    /**
     * The main debugger version.
     */
    private readonly version = '1.0.1';

    /**
     * The paths of the configuration files.
     */
    private tsconfigBaseFilePath: string;
    private tsconfigFilePath: string;
    private angularJsonFilePath: string;
    private gitIgnoreFilePath: string;
    private packageJsonFilePath: string;
    private versionJsonFilePath: string;
    private packageLockJsonFilePath: string;
    private nodeModulesFolderPath: string;
    private e2eFolderPath: string;
    private testTsFilePath: string;
    private testTsFileTemplatePath: string;
    private cIBuildYmlFilePath: string;
    private pullRequestYmlFilePath: string;
    private srcTsConfigSpecFilePath: string;
    private srcTsConfigLibProdFilePath: string;
    private gulpCompileTsFilePath: string;
    private gulpCompileTsTemplatePath: string;
    private gulpIndexTsFilePath: string;
    private gulpIndexTsTemplatePath: string;
    private uiTestAutomationLegacyFilePath: string;
    private uiTestAutomationLegacyFolderPath: string;
    private logFilePath: string;
    private eslintrcFilePath: string;

    /**
     * The upgrader for each configuration file.
     */
    private packageJsonUpgrader: PackageJsonUpgrader;
    private versionJsonUpgrader: VersionJsonUpgrader;
    private tSConfigUpgrader: TSConfigUpgrader;
    private tSConfigBaseUpgrader: TSConfigUpgrader;
    private gitIgnoreUpgrader: GitIgnoreUpgrader;
    private angularJsonUpgrader: AngularJsonUpgrader;
    private testTsUpgrader: FileReplacementUpgrader;
    private cIBuildUpgrader: CIBuildYmlUpgrader;
    private pullRequestUpgrader: CIBuildYmlUpgrader;
    private srcTsConfigLibProdUpgrader: ReplaceUpgrader;
    private srcTsConfigSpecUpgrader: RemoveUpgrader;
    private gulpCompileTsUpgrader: FileReplacementUpgrader;
    private gulpIndexTsUpgrader: FileReplacementUpgrader;
    private logParser: LogParser;
    private jasmineTestUpgrader: JasmineTestUpgrader;
    private uITestAutomationLegacyUpgrader: UITestAutomationLegacyUpgrader;
    /**
     * The eslint fix upgrader to support linter fix command for stage 4.
     */
    private eslintAutoFixUpgrader: EslintAutoFixUpgrader;

    /**
     * The file names for each configuration file.
     */
    private readonly tsconfigBaseFileName = 'tsconfig.base.json';
    private readonly tsconfigFileName = 'tsconfig.json';
    private readonly packageJsonFileName = 'package.json';
    private readonly versionJsonFileName = 'version.json';
    private readonly packageLockJsonFileName = 'package-lock.json';
    private readonly angularFileName = 'angular.json';
    private readonly e2eFolderName = 'e2e';
    private readonly nodeModulesFolderName = 'node_modules';
    private readonly gitIgnoreFileName = '.gitignore';
    private readonly testTsFileName = 'test.ts';
    private readonly cIBuildYmlFileName = 'ci-build.yml';
    private readonly pullRequestYmlFileName = 'pull-request.yml';
    private readonly srcTsConfigLibProdFileName = 'tsconfig.lib.prod.json';
    private readonly srcTsConfigSpecFileName = 'tsconfig.spec.json';
    private readonly gulpCompileTsFileName = 'compile.ts';
    private readonly gulpIndexTsFileName = 'index.ts';
    private readonly logFileName = 'log.txt';
    private readonly eslintrcFileName = '.eslintrc.json';

    constructor() {
        Logger.log(Angular15UpgraderMessages.StartingUpgrade);
        Common.rootPath = process.cwd();
        Logger.log(Common.rootPath);
    }

    /**
     * The upgrader main function.
     * If only runs the unit test
     * node ./node_modules/@microsoft/windows-admin-center-sdk/tools/wac-cli/src/index.js angular15Upgrade --pipeline false --stage 2
     * @param pipeline It determines whether to run this command on the pipeline.
     * @param debug It determines whether to run the debug mode.
     * @param internal It determines whether to run angualr 15 upgrade for the internal extension or external sdk.
     * @param stage '0' - the default Angular 15 upgrade stage,
     *              '1' - the Angular core stage,
     *              '2' - the Angular 15 unit test stage,
     *              '3' - the legacy ui-test-automation stage.
     *              '4' - the eslint to support gulp lintApp --fix command.
     */
    public async upgrade(pipeline = false, stage = 0, internal = false, debug = false) {
        this.debug = !!debug;
        this.pipeline = !!pipeline;
        this.internal = !!internal;
        Logger.log(this.version);
        Logger.log(Angular15UpgraderMessages.StartingUpgrade);
        Logger.log('Passed in parameters: ' + this.pipeline + ' ' + stage + ' ' + this.internal + ' ' + this.debug );

        this.mainDebugger = this.debug ? new MainDebugger(): undefined;

        Logger.log(Angular15UpgraderMessages.UpdateBasicPaths);
        this.updateBasicPaths();

        Logger.log(Angular15UpgraderMessages.Initialization);
        this.initialization();

        switch (stage) {
            case 0:
                Logger.log(Angular15UpgraderMessages.StartMessageStage0);
                await this.criticalConfigurationFilesUpdates();
                await this.installDependenciesPreparation();
                await this.installLKGElectronToChromium();
                await this.installDependencies();
                // Fix any eslint errors that doesn't match the latest eslint rules.
                await this.fixEslintErrors(this.pipeline);
                await this.gulpQuick();
                await this.gulpBuildPostAudit();
                // Fix any eslint errors that is triggered by audit.
                await this.fixEslintErrors(this.pipeline);
                await this.gulpBuildProd();
                await this.gulpBuildProdPostAudit();
                // Fix any eslint errors clean-ups.
                await this.fixEslintErrors(this.pipeline);
                await this.jasmineTestUpgrader.run();
                break;
            case 1:
                Logger.log(Angular15UpgraderMessages.StartMessageStage1);
                await this.criticalConfigurationFilesUpdates();
                await this.installDependenciesPreparation();
                await this.installLKGElectronToChromium();
                await this.installDependencies();
                // Fix any eslint errors that doesn't match the latest eslint rules.
                await this.fixEslintErrors(this.pipeline);
                await this.gulpQuick();
                await this.gulpBuildPostAudit();
                // Fix any eslint errors that is triggered by audit.
                await this.fixEslintErrors(this.pipeline);
                await this.gulpBuildProd();
                await this.gulpBuildProdPostAudit();
                // Fix any eslint errors clean-ups.
                await this.fixEslintErrors(this.pipeline);
                break;
            case 2:
                Logger.log(Angular15UpgraderMessages.StartMessageStage2);
                await this.gulpIndexTsUpgrader.run();
                await this.testTsUpgrader.run();
                await this.jasmineTestUpgrader.run();
                break;
            case 3:
                Logger.log(Angular15UpgraderMessages.StartMessageStage3);
                if (!this.internal) {
                    Logger.log(Angular15UpgraderMessages.InternalCommandOnly);
                } else {
                    await this.packageJsonUpgrader.run(this.pipeline),
                    await this.installUTALegacyDevDependency();
                    await this.uITestAutomationLegacyUpgrader.run();
                    await this.installDependenciesForUITestAutomation();
                }
            case 4:
                Logger.log(Angular15UpgraderMessages.StartMessageStage4);
                if (!this.internal) {
                    Logger.log(Angular15UpgraderMessages.InternalCommandOnly);
                } else {
                    await this.eslintAutoFixUpgrader.run();
                    await this.gulpLintFix();
                }
            default:
                Logger.log('default');
                break;
        }
    }

    protected async criticalConfigurationFilesUpdates(): Promise<void> {
        Promise.all([
            this.packageJsonUpgrader.run(this.pipeline),
            this.versionJsonUpgrader.run(),
            this.tSConfigBaseUpgrader.runBase(),
            this.tSConfigUpgrader.run(),
            this.gitIgnoreUpgrader.run(),
            this.angularJsonUpgrader.run(),
            this.testTsUpgrader.run(),
            this.cIBuildUpgrader.run(),
            this.pullRequestUpgrader.run(),
            this.srcTsConfigLibProdUpgrader.run(),
            this.srcTsConfigSpecUpgrader.run(),
            this.gulpCompileTsUpgrader.run(),
            this.gulpIndexTsUpgrader.run()
        ]).then((results) => {
            console.log('All Promises have completed:', results);

            if (this.debug) {
                this.mainDebugger.verifyTestData(this).then(() => {
                    Logger.log(Angular15UpgraderMessages.Completed);
                });
            }
        })
        .catch((error) => {
            Logger.error('At least one Promise encountered an error:' + error);
        });
    }

    protected initialization() {
        this.packageJsonUpgrader = new PackageJsonUpgrader(this.packageJsonFilePath);
        this.versionJsonUpgrader = new VersionJsonUpgrader(this.versionJsonFilePath);
        this.tSConfigBaseUpgrader = new TSConfigUpgrader(this.tsconfigBaseFilePath, this.tsconfigBaseFileName);
        this.tSConfigUpgrader = new TSConfigUpgrader(this.tsconfigFilePath, this.tsconfigFileName);
        this.gitIgnoreUpgrader = new GitIgnoreUpgrader(this.gitIgnoreFilePath);
        this.angularJsonUpgrader = new AngularJsonUpgrader(this.angularJsonFilePath);
        this.testTsUpgrader = new FileReplacementUpgrader(this.testTsFileTemplatePath, this.testTsFilePath);
        this.cIBuildUpgrader = new CIBuildYmlUpgrader(this.cIBuildYmlFilePath, this.cIBuildYmlFileName);
        this.pullRequestUpgrader = new CIBuildYmlUpgrader(this.pullRequestYmlFilePath, this.pullRequestYmlFileName);
        this.srcTsConfigLibProdUpgrader = new ReplaceUpgrader(this.srcTsConfigLibProdFilePath, srcTsConfigLibProdReplaces, this.srcTsConfigLibProdFileName);
        this.srcTsConfigSpecUpgrader = new RemoveUpgrader(this.srcTsConfigSpecFilePath, srcTsConfigSpecRemoves, this.srcTsConfigSpecFileName);
        this.gulpCompileTsUpgrader = new FileReplacementUpgrader(this.gulpCompileTsTemplatePath, this.gulpCompileTsFilePath);
        this.gulpIndexTsUpgrader = new FileReplacementUpgrader(this.gulpIndexTsTemplatePath, this.gulpIndexTsFilePath);
        this.jasmineTestUpgrader = new JasmineTestUpgrader(this.testTsFilePath, this.debug);
        this.uITestAutomationLegacyUpgrader = new UITestAutomationLegacyUpgrader(this.uiTestAutomationLegacyFilePath, this.packageJsonFileName);
        this.eslintAutoFixUpgrader = new EslintAutoFixUpgrader(this.eslintrcFilePath);

        this.logParser = new LogParser(this.logFilePath, this.logFileName);
    }

    protected updateBasicPaths(): void {
        this.tsconfigBaseFilePath = Common.rootPath + '\\' + this.tsconfigBaseFileName;
        this.tsconfigFilePath = Common.rootPath + '\\' + this.tsconfigFileName;
        this.angularJsonFilePath = Common.rootPath + '\\' + this.angularFileName;
        this.packageJsonFilePath = Common.rootPath + '\\' + this.packageJsonFileName;
        this.versionJsonFilePath =  Common.rootPath + '\\' + this.versionJsonFileName;
        this.packageLockJsonFilePath = Common.rootPath + '\\' + this.packageLockJsonFileName;
        this.nodeModulesFolderPath = Common.rootPath + '\\' + this.nodeModulesFolderName;
        this.e2eFolderPath = Common.rootPath + '\\' + this.e2eFolderName;
        this.gitIgnoreFilePath = Common.rootPath + '\\' + this.gitIgnoreFileName;
        this.eslintrcFilePath = Common.rootPath + '\\' + this.eslintrcFileName;

        this.srcTsConfigLibProdFilePath = Common.rootPath + '\\src\\' + this.srcTsConfigLibProdFileName;
        this.srcTsConfigSpecFilePath = Common.rootPath + '\\src\\' + this.srcTsConfigSpecFileName;

        this.cIBuildYmlFilePath = Common.rootPath + '\\build\\pipelines\\' + this.cIBuildYmlFileName;
        this.pullRequestYmlFilePath = Common.rootPath + '\\build\\pipelines\\' + this.pullRequestYmlFileName;

        this.testTsFilePath = Common.rootPath + '\\src\\' + this.testTsFileName;
        this.testTsFileTemplatePath = Common.cliRootPath.replace('wac-cli', 'module-template\\angular15\\test.ts');

        this.gulpCompileTsFilePath = Common.rootPath + '\\gulpfile.ts\\common\\' + this.gulpCompileTsFileName;
        this.gulpCompileTsTemplatePath = Common.cliRootPath.replace('wac-cli', 'module-template\\angular15\\gulpfile.ts\\common\\compile.ts');

        this.gulpIndexTsFilePath = Common.rootPath + '\\gulpfile.ts\\' + this.gulpIndexTsFileName;
        this.gulpIndexTsTemplatePath = Common.cliRootPath.replace('wac-cli', 'module-template\\angular15\\gulpfile.ts\\index.ts');

        this.uiTestAutomationLegacyFilePath = Common.rootPath + '\\ui-test-automation\\' + this.packageJsonFileName;
        this.uiTestAutomationLegacyFolderPath = Common.rootPath + '\\ui-test-automation\\';

        this.logFilePath = Common.rootPath + '\\' + this.logFileName;

        if (this.debug) {
            this.mainDebugger.updateTestData(this);
        }
    }

    protected async gulpBuildPostAudit(): Promise<void> {
        return this.logParser.run();
    }

    protected gulpLintFix(): Promise<void> {
        const gulpLintFixCmd = 'gulp lintApp --fix';
        Logger.log(gulpLintFixCmd);
        return runCommand(gulpLintFixCmd, this.debug, true, Angular15UpgraderMessages.Completed, this.logFileName).then((response) => {
            console.log(response);
        });
    }

    protected gulpQuick(): Promise<void> {
        const gulpQuick = 'gulp quick';
        Logger.log(gulpQuick);
        return runCommand(gulpQuick, this.debug, true, Angular15UpgraderMessages.Completed, this.logFileName).then((response) => {
            console.log(response);
        });
    }

    protected gulpBuildProd(): Promise<void> {
        const gulpBuild = 'gulp build --prod';
        Logger.log(gulpBuild);
        return runCommand(gulpBuild, this.debug, true, Angular15UpgraderMessages.Completed, this.logFileName).then((response) => {
            console.log(response);
        });
    }

    protected async gulpBuildProdPostAudit(): Promise<void> {
        return this.logParser.run();
    }

    protected async installUTALegacyDevDependency(): Promise<void> {
        const setprefixCmd = 'npm config set save-prefix=';
        Logger.log(setprefixCmd);
        await runCommand(setprefixCmd, this.debug);
        const cmd = 'npm install @msft-sme/ui-test-automation@latest --save-dev --force';
        Logger.log(cmd);
        await runCommand(cmd, this.debug);
    }

    protected async installDependenciesPreparation(): Promise<void> {
        await this.removeNodeModuleFolder();
        await this.removePackageLockJsonFile();
        await this.removeE2EFolder();
        await this.checkEslintRCFile();
        updateShellReferences('', this.internal);
    }

    private async checkEslintRCFile(): Promise<void> {
        const esLintUpgrader = new EslintUpgrader();
        await esLintUpgrader.replaceEslintConfig();
    }

    private async installMicrosftSignalR(): Promise<void> {
        const cmd = 'npm install @microsoft/signalr@6.0.8 --save';

        Logger.log(cmd);
        await runCommand(cmd, this.debug);
    }

    private async removePackageLockJsonFile(): Promise<void> {
        const cmd = `del ${this.packageLockJsonFilePath}`;

        Logger.log(cmd);
        await runCommand(cmd, this.debug);
    }

    private async removeNodeModuleFolder(): Promise<void> {
        const cmd = `rmdir /s /q ${this.nodeModulesFolderPath}`;

        Logger.log(cmd);
        await runCommand(cmd, this.debug);
    }

    private async removeE2EFolder(): Promise<void> {
        const cmd = `rmdir /s /q ${this.e2eFolderPath}`;

        Logger.log(cmd);
        await runCommand(cmd, this.debug);
    }

    private async installDependencies(): Promise<void> {
        // Needs to be installed on the SDK root dependency.
        if (!this.internal) {
            await this.installMicrosftSignalR();
        }
        const cmd = 'npm install --force';

        Logger.log(cmd);
        await runCommand(cmd, this.debug);
    }

    private async installDependenciesForUITestAutomation(): Promise<void> {
        const cmd = 'pushd ' + this.uiTestAutomationLegacyFolderPath + '&& del package-lock.json && npm install && popd';
        await runCommand(cmd, this.debug);
    }

    private async fixEslintErrors(ignore = false): Promise<void> {
        if (ignore) {
            return;
        }
        
        const rootFileLinterFix = 'npx eslint . --fix';
        Logger.log(rootFileLinterFix);
        await runCommand(rootFileLinterFix, this.debug, true, Angular15UpgraderMessages.FinalizeInfoLintFix);
        const projectLinterFix = 'npx ng lint --fix';
        Logger.log(projectLinterFix);
        await runCommand(projectLinterFix, this.debug, true, Angular15UpgraderMessages.FinalizeInfoLintFix);
    }

    /**
     * electron-to-chromium 1.4.533 - 1.4.535 are bad versions that is also the latest, meaning any dependency uses ^1.4.xxx are also blocked,
     * below are the dependency list:
     * /@angular-devkit/build-angular@15.2.8 -> browserslist@4.21.5 that has dependency on ^1.4.284(which always grab 1.4.535)
     * always force 1.4.532
     */
    private async installLKGElectronToChromium(): Promise<void> {
        const cmd = 'npm install electron-to-chromium@1.4.532 --force';
        Logger.log(cmd);
        await runCommand(cmd, this.debug);
    }
}
